import { useEffect, useMemo, useState } from 'react';
import { Heading, CTABlock, GridContainer, LinkProvider } from '@lululemon/b2b-ecom-pattern-library';
import categoryQueryParams from 'helpers/categoryQueryParam';
import { ConditionalLink } from 'helpers/cocoLink';
import { useProduct } from 'helpers/hooks/useProduct';
import { findCategoryBySlug } from 'helpers/utils/categorySlugUtils';
import { HOME_PAGE } from 'helpers/utils/googleAnalyticsEvents';
import { useFeatureFlags, FEATURE_FLAG_LIST } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';
import Skeleton from 'react-loading-skeleton';
import { ProductCard } from 'shared/types/homePage';
import { useProductList } from 'ui/cdp/context';
import { useAccount } from 'frontastic/index';
import styles from './quick-links-group.module.scss';

type QuickLinkProps = {
  heading: string;
  subContent: string;
  product?: ProductCard;
  categorySlug?: string;
  path?: string;
  categoryId?: string;
};

export type QuickLinksProps = {
  quickLinksTitle: string;
  quickLinks: QuickLinkProps[];
};

const QuickLinksGroup = ({ quickLinksTitle, quickLinks }: QuickLinksProps) => {
  const { categories, getProductForQuickLink } = useProduct();
  const [isLoading, setLoading] = useState(false);
  const [quickLinksData, setQuickLinksData] = useState<QuickLinkProps[]>([]);
  const { limitStep } = useProductList();
  const { account } = useAccount();
  const { isFeatureEnabled } = useFeatureFlags();
  const isQuickLinksRefreshFixEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.QUICK_LINKS_REFRESH_FIX);
  const legacyCategoryParams = useMemo(() => categoryQueryParams(account, limitStep, null), [account, limitStep]);
  const fixedCategoryParams = categoryQueryParams(account, limitStep, null).toString();
  const categoryParams = isQuickLinksRefreshFixEnabled ? fixedCategoryParams : legacyCategoryParams;
  const hasRenderableLinks = quickLinksData.some((link) => Boolean(link.product?.images?.src && link.path));
  //Google Analytics
  const { trackEvent, EVENT_CATEGORY } = useAnalytics();

  const isHomepageHyperlinks = isFeatureEnabled(FEATURE_FLAG_LIST.ENABLE_HOMEPAGE_HYPERLINKS);
  const isQuickLinksMissingDataGuardEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.DC_MOVE_SITE_MANAGEMENT);

  useEffect(() => {
    if (!isQuickLinksMissingDataGuardEnabled) {
      return;
    }

    const fetchProducts = async () => {
      if (!quickLinks.length || !categories.length) {
        setQuickLinksData((prev) => (prev.length ? [] : prev));
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const linksToProcess: QuickLinkProps[] = [];

        if (isHomepageHyperlinks) {
          quickLinks.forEach((link) => {
            if (!link.categorySlug) return;

            const matchedCategory = findCategoryBySlug(link.categorySlug, categories);

            if (matchedCategory) {
              link.categoryId = matchedCategory.categoryId;
              link.categorySlug = matchedCategory.slug;
              link.path = `${matchedCategory.path}?${categoryParams}`;
              linksToProcess.push(link);
            }
          });
        } else {
          quickLinks.forEach((link) => {
            const categoryResult = categories.find((item) => item.slug === link.categorySlug);
            if (categoryResult?.categoryId) {
              link.categoryId = categoryResult.categoryId;
              link.categorySlug = categoryResult.slug;
              link.path = `${categoryResult.path}?${categoryParams}`;
            }
            linksToProcess.push(link);
          });
        }

        const categoryIds = Array.from(
          new Set(linksToProcess.map((link) => link.categoryId).filter(Boolean) as string[]),
        );

        const response = await getProductForQuickLink(categoryIds);

        if (Array.isArray(response) && response.length > 0) {
          const productMap = new Map(response.map((item) => [item.categoryId, item]));

          linksToProcess.forEach((link) => {
            const productData = productMap.get(link.categoryId);
            link.product = productData ? productData.products[0] : undefined;
          });
        }

        setQuickLinksData(linksToProcess);
      } catch (error) {
        setQuickLinksData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    categories,
    categoryParams,
    getProductForQuickLink,
    isHomepageHyperlinks,
    isQuickLinksMissingDataGuardEnabled,
    quickLinks,
  ]);

  useEffect(() => {
    if (isQuickLinksMissingDataGuardEnabled) {
      return;
    }

    const fetchProducts = async () => {
      setLoading(true);

      if (quickLinks.length && categories.length) {
        try {
          const linksToProcess: QuickLinkProps[] = [];

          if (isHomepageHyperlinks) {
            quickLinks.forEach((link) => {
              if (!link.categorySlug) return;

              const matchedCategory = findCategoryBySlug(link.categorySlug, categories);

              if (matchedCategory) {
                link.categoryId = matchedCategory.categoryId;
                link.categorySlug = matchedCategory.slug;
                link.path = `${matchedCategory.path}?${categoryParams}`;
                linksToProcess.push(link);
              }
            });
          } else {
            quickLinks.forEach((link) => {
              const categoryResult = categories.find((item) => item.slug === link.categorySlug);
              if (categoryResult?.categoryId) {
                link.categoryId = categoryResult.categoryId;
                link.categorySlug = categoryResult.slug;
                link.path = `${categoryResult.path}?${categoryParams}`;
              }
              linksToProcess.push(link);
            });
          }

          const categoryIds = Array.from(
            new Set(linksToProcess.map((link) => link.categoryId).filter(Boolean) as string[]),
          );

          const response = await getProductForQuickLink(categoryIds);

          if (Array.isArray(response) && response.length > 0) {
            const productMap = new Map(response.map((item) => [item.categoryId, item]));

            linksToProcess.forEach((link) => {
              const productData = productMap.get(link.categoryId);
              link.product = productData ? productData.products[0] : undefined;
            });
          }

          setQuickLinksData(linksToProcess);
          setLoading(false);
        } catch (error) {
          setLoading(false);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProducts();
  }, [categories.length, isHomepageHyperlinks, isQuickLinksMissingDataGuardEnabled]);

  const headingContent = isQuickLinksMissingDataGuardEnabled ? (
    isLoading || hasRenderableLinks ? (
      <Heading className="lll-type-heading-medium" tag="h2" data-testid="quick-group-links__heading_test-id">
        {isLoading ? <Skeleton className={styles.quickLinksHeadingLoading} count={1} /> : quickLinksTitle}
      </Heading>
    ) : undefined
  ) : (
    <Heading className="lll-type-heading-medium" tag="h2" data-testid="quick-group-links__heading_test-id">
      {isLoading ? <Skeleton className={styles.quickLinksHeadingLoading} count={1} /> : quickLinksTitle}
    </Heading>
  );

  if (!isLoading && !hasRenderableLinks) {
    return null;
  }

  return (
    <GridContainer layout="2 Columns - Wide Split" className={styles.gridContainer} heading={headingContent}>
      {isLoading ? (
        quickLinks?.map((link: QuickLinkProps, index) => (
          <Skeleton key={index} className={styles.quickLinksLoading} count={1} />
        ))
      ) : (
        <LinkProvider component={ConditionalLink}>
          {quickLinksData?.map(
            (link: QuickLinkProps, index) =>
              link.product?.images?.src &&
              link?.path && (
                <CTABlock
                  imageProps={{
                    src: link.product?.images?.src,
                    lazy: false,
                  }}
                  heading={link?.heading}
                  subContent={link?.subContent}
                  data-testid={`quick-group-links__link_${index}_test-id`}
                  className={styles.ctaBlock}
                  href={link?.path}
                  key={`quick-link-${index}`}
                  onClick={() => {
                    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, HOME_PAGE.QUICK_LINKS_CTA_CLICKED(link?.heading));
                  }}
                />
              ),
          )}
        </LinkProvider>
      )}
    </GridContainer>
  );
};

export default QuickLinksGroup;
